import { publicFetcher, staticOptions } from "lib/core/apiClient";
import {
  MerchantUIAttributes,
  MerchantUIConfig,
  UIConfigAttributes,
  MerchantUIResponseType,
  CheckoutMetadata,
  CartMetadata,
} from "lib/types/merchantConfigTypes";
import { eventTypes } from "lib/utils/constants";
import { customBranding } from "lib/utils/customBranding";
import { publishPostMessage } from "lib/utils/helpers";
import React, { createContext, useContext, useEffect, useState, useReducer, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";

// Types
export enum MerchantUIConfigActionType {
  SET_MERCHANT_ATTRIBUTES = "SET_MERCHANT_ATTRIBUTES",
  SET_VALIDATING = "SET_VALIDATING",
  SET_CHECKOUT_METADATA = "SET_CHECKOUT_METADATA",
  SET_CART_METADATA = "SET_CART_METADATA",
}

export interface MerchantUIConfigState {
  isValidating: boolean;
  attributes: MerchantUIAttributes;
  checkoutUIAttributes: UIConfigAttributes;
  cartUIAttributes: UIConfigAttributes;
  checkoutMetadata: CheckoutMetadata;
  cartMetadata: CartMetadata;
  accountId: string | null;
}

// Initial state
const merchantUIConfigIntialState: MerchantUIConfigState = {
  accountId: null,
  isValidating: true,
  checkoutUIAttributes: {
    logo_url: "",
    banner_config: [],
    primary_cta_disability_rule: {},
    item_modification_rule: {},
  },
  cartUIAttributes: {
    logo_url: "",
    banner_config: [],
    primary_cta_disability_rule: "",
    item_modification_rule: {},
  },
  attributes: {
    is_one_page_checkout: false,
  },
  checkoutMetadata: {
    metadataLoading: true,
  },
  cartMetadata: {
    metadataLoading: true,
  },
};

export interface MerchantUIContext {
  state: MerchantUIConfigState;
  actions: {
    setFallbackMerchantData: (data: any) => void;
  };
}

// Context
const MerchantContext = createContext<MerchantUIContext>({
  state: merchantUIConfigIntialState,
  actions: {
    setFallbackMerchantData: () => {},
  },
});

// Actions
type MerchantAction =
  | {
      type: MerchantUIConfigActionType.SET_MERCHANT_ATTRIBUTES;
      payload: Omit<MerchantUIResponseType, "isValidating">;
    }
  | { type: MerchantUIConfigActionType.SET_VALIDATING; payload: boolean }
  | {
      type: MerchantUIConfigActionType.SET_CHECKOUT_METADATA;
      payload: CheckoutMetadata;
    }
  | {
      type: MerchantUIConfigActionType.SET_CART_METADATA;
      payload: CartMetadata;
    };

const getUIAttributes = (
  uiConfig: MerchantUIConfig[],
): {
  checkoutUIAttributes: UIConfigAttributes;
  cartUIAttributes: UIConfigAttributes;
} => {
  const checkout = uiConfig.filter(
    (config) => config.sf_context.toLowerCase() === "checkout" && config.channel === "web",
  );
  const cart = uiConfig.filter(
    (config) => config.sf_context.toLowerCase() === "cart" && config.channel === "web",
  );

  return {
    checkoutUIAttributes: checkout[0]?.ui_attributes ?? merchantUIConfigIntialState.checkoutUIAttributes,
    cartUIAttributes: cart[0]?.ui_attributes ?? merchantUIConfigIntialState.cartUIAttributes,
  };
};

// Reducer
function merchantReducer(state: MerchantUIConfigState, action: MerchantAction): MerchantUIConfigState {
  switch (action.type) {
    case MerchantUIConfigActionType.SET_MERCHANT_ATTRIBUTES: {
      //updating branding colors
      const ui: any = action.payload?.attributes?.ui;
      if (Boolean(ui)) {
        customBranding(
          ui?.color_pallet?.primary_color,
          ui?.color_pallet?.secondary_color,
          ui?.color_pallet?.primary_text_color,
        );
      }

      return {
        ...state,
        accountId: action.payload?.account_id,
        attributes: action.payload?.attributes ?? merchantUIConfigIntialState.attributes,
        checkoutUIAttributes: getUIAttributes(action.payload?.ui_config ?? []).checkoutUIAttributes,
        cartUIAttributes: getUIAttributes(action.payload?.ui_config ?? []).cartUIAttributes,
        isValidating: false,
      };
    }

    case MerchantUIConfigActionType.SET_CHECKOUT_METADATA: {
      const defaultView = action.payload?.default_view === null ? "ADDRESS" : action.payload?.default_view;
      return {
        ...state,
        checkoutMetadata: {
          ...(action.payload ?? merchantUIConfigIntialState.checkoutMetadata),
          default_view: defaultView,
          metadataLoading: false,
        },
      };
    }

    case MerchantUIConfigActionType.SET_CART_METADATA: {
      return {
        ...state,
        cartMetadata: {
          ...(action.payload ?? merchantUIConfigIntialState.checkoutMetadata),
          metadataLoading: false,
        },
      };
    }

    case MerchantUIConfigActionType.SET_VALIDATING:
      return {
        ...state,
        isValidating: action.payload,
      };
    default:
      return state;
  }
}

// Provider
export const MerchantUIConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(merchantReducer, merchantUIConfigIntialState);
  const [searchParams] = useSearchParams();
  const shopUrl = searchParams.get("shop_url");

  const { data: merchantData, error } = useSWR<MerchantUIResponseType>(
    shopUrl ? `/v1/merchant-apps?shop-url=${shopUrl}` : null,
    publicFetcher,
    staticOptions,
  );

  const handleMerchantData = (event: any) => {
    if (event.data.type === eventTypes.MERCHANT_ATTRIBUTES) {
      const merchantAttributes = event?.data?.payload?.merchantAttributes;
      if (merchantAttributes) {
        dispatch({
          type: MerchantUIConfigActionType.SET_MERCHANT_ATTRIBUTES,
          payload: merchantAttributes,
        });
      }

      const metadata = event?.data?.payload?.uiConfig?.data;
      const context = event?.data?.payload?.context;

      switch (context) {
        case "checkout":
          dispatch({
            type: MerchantUIConfigActionType.SET_CHECKOUT_METADATA,
            payload: {
              ...(metadata ?? {}),
            },
          });
          break;

        case "cart":
          dispatch({
            type: MerchantUIConfigActionType.SET_CART_METADATA,
            payload: {
              ...(metadata ?? {}),
            },
          });
          break;
      }
      publishPostMessage(eventTypes.CLOSE_MERCHANT_SUBSCRIPTION, {});
    }
  };

  // For faster loading we take merchant data from parent
  useEffect(() => {
    window.addEventListener("message", handleMerchantData);

    return () => {
      window.removeEventListener("message", handleMerchantData);
    };
  }, []);

  // If we don't get data or if third party cookies are blocked
  // Fallback is merchant-apps API call data
  useEffect(() => {
    if (merchantData) {
      dispatch({ type: MerchantUIConfigActionType.SET_MERCHANT_ATTRIBUTES, payload: merchantData });
    }
  }, [merchantData, error]);

  const setFallbackMerchantData = (data: any) => {
    dispatch({
      type: MerchantUIConfigActionType.SET_MERCHANT_ATTRIBUTES,
      payload: data,
    });
  };

  const actions = {
    setFallbackMerchantData,
  };

  return (
    <MerchantContext.Provider
      value={{
        state: state,
        actions,
      }}>
      {children}
    </MerchantContext.Provider>
  );
};

export const useMerchantUiConfig = () => {
  const context = useContext(MerchantContext);
  return context;
};
